import { RFP_OFFER_MEMBER_ROLE_ADMIN } from '@/utils/constants/rfp/rfpOfferMember/role';
import { get } from '@/utils/libs/lodash';
import { composeValidators } from '@/utils/libs/react-final-form/validate';
import { isRfpRoleAdmin } from '@/utils/resources/rfp/rfpRoles';
import { ACCEPT_PDF_MIME_TYPE, hasArrayItem, isAllowAcceptTypeFile, isAllowMegaSizeFile, isFileValid, isInteger, isMore, isNumberWithinRange, isTrue, isRequiredText, strMax, isEmail, } from '@/utils/validate/conditions';
/**
 * 下書きかどうかの判定
 * @param draftCheckedFunc
 */
export const draftCheck = (draftCheckedFunc) => (value, allValues) => {
    const isDraft = 'is_draft' in allValues && !!(allValues === null || allValues === void 0 ? void 0 : allValues.is_draft);
    return draftCheckedFunc(isDraft)(value, allValues);
};
/**
 * validation: 利用規約同意確認
 * @param isDraft draftCheck の結果受け取り
 */
export const validateOfferAssign = (isDraft) => composeValidators(isDraft ? null : isTrue('利用規約に同意してください'));
/**
 * validation: 提案資料（new id）
 * @param fieldNameOfId id の field name
 * @param isDraft draftCheck の結果受け取り
 */
export const validateOfferFileOfNewId = (fieldNameOfId) => (isDraft) => composeValidators(isDraft
    ? null
    : (value, allValues) => {
        const id = get(allValues, fieldNameOfId || '');
        const errorMessage = '提案資料を選択してください';
        return id ? undefined : isRequiredText(errorMessage)(value);
    });
/**
 * validation: 提案資料（file）
 */
export const validateOfferFileOfFile = composeValidators(isFileValid(), isAllowMegaSizeFile(100, '100MB以下のファイルを選択してください'), isAllowAcceptTypeFile(ACCEPT_PDF_MIME_TYPE, 'PDFファイルを選択してください'));
/**
 * validation: 参考資料（file）
 */
export const validateOfferAdditionalFileOfFile = composeValidators(isFileValid(), isAllowMegaSizeFile(100, '100MB以下のファイルを選択してください'));
/**
 * validation: 概要
 * @param isDraft draftCheck の結果受け取り
 */
export const validateOfferSummary = (isDraft) => composeValidators(isDraft ? null : isRequiredText('Executive Summaryを入力してください'), 
// NOTE: バリデーションは700文字、表示上は500文字
// NOTE: JSとWordで文字数計算方法が異なり、その差異分を許容するため（コンサルの方が Word で文字数計算をするため）
strMax(700, '500文字以内で入力してください'));
/**
 * validation: その他説明
 */
export const validateOfferFreeDescription = composeValidators(strMax(1000, '1000文字以内で入力してください'));
/**
 * validation: 回答内容
 * @param isDraft draftCheck の結果受け取り
 */
export const validateOfferAnswer = (isDraft) => composeValidators(isDraft ? null : isRequiredText('ご回答内容を入力してください'), 
// NOTE: バリデーションは1500文字、表示上は1000文字
// NOTE: JSとWordで文字数計算方法が異なり、その差異分を許容するため（コンサルの方が Word で文字数計算をするため）
strMax(1500, '1000文字以内で入力してください'));
/**
 * validation: 面談予定日
 * @param isDraft draftCheck の結果受け取り
 */
export const validateOfferInterviewDate = (isDraft) => composeValidators(isDraft ? null : isRequiredText('面談希望日を入力してください'), strMax(1000, '1000文字以内で入力してください'));
/**
 * validation: 期間
 * @param fieldNameOfMaxPeriod 最大期間入力フォーム field name
 */
export const validateOfferPeriod = (fieldNameOfMaxPeriod) => (isDraft) => composeValidators(isDraft
    ? null
    : (value, allValues) => {
        const maxPeriod = get(allValues, fieldNameOfMaxPeriod || '');
        return maxPeriod
            ? undefined
            : isRequiredText('想定期間を入力してください')(value);
    }, isInteger('想定期間は整数で入力してください'), isNumberWithinRange(1, 99, '想定期間は1週間から99週間の数値で入力してください'));
/**
 * validation: 最大期間
 * @param fieldNameOfPeriod 期間の入力フォーム field name
 */
export const validateOfferMaxPeriod = (fieldNameOfPeriod) => (isDraft) => composeValidators(isDraft
    ? null
    : (value, allValues) => {
        const period = get(allValues, fieldNameOfPeriod || '');
        const errorMessage = '最大想定期間を入力してください';
        return period ? undefined : isRequiredText(errorMessage)(value);
    }, isInteger('最大想定期間は整数で入力してください'), (value, allValues) => {
    const period = get(allValues, fieldNameOfPeriod || '');
    const errorMessage = '最大想定期間は想定期間より大きい数値を入力してください';
    return period ? isMore(period, errorMessage)(value) : undefined;
}, isNumberWithinRange(1, 99, '最大期想定間は1週間から99週間の数値で入力してください'));
/**
 * validation: 報酬額（費用）
 * @param isDraft draftCheck の結果受け取り
 */
export const validateOfferPrice = (fieldNameOfMaxPrice) => (isDraft) => composeValidators(isDraft
    ? null
    : (value, allValues) => {
        const maxPeriod = get(allValues, fieldNameOfMaxPrice || '');
        return maxPeriod
            ? undefined
            : isRequiredText('想定費用を入力してください')(value);
    }, isInteger('想定費用は整数で入力してください'), isNumberWithinRange(1, 99999, '想定費用は1万円から99999万円の数値で入力してください'));
/**
 * validation: 最大報酬額（最大費用）
 * @param isDraft draftCheck の結果受け取り
 */
export const validateOfferMaxPrice = (fieldNameOfPrice) => (isDraft) => composeValidators(isDraft
    ? null
    : (value, allValues) => {
        const period = get(allValues, fieldNameOfPrice || '');
        const errorMessage = '最大想定費用を入力してください';
        return period ? undefined : isRequiredText(errorMessage)(value);
    }, isInteger('最大想定費用は整数で入力してください'), (value, allValues) => {
    const period = get(allValues, fieldNameOfPrice || '');
    const errorMessage = '最大想定費用は想定費用より大きい数値を入力してください';
    return period ? isMore(period, errorMessage)(value) : undefined;
}, isNumberWithinRange(1, 99999, '最大想定費用は1万円から99999万円の数値で入力してください'));
/**
 * validation: チーム体制
 * @param fieldKeyNameOfRole 役割入力フォーム field name
 * @returns
 */
export const validateOfferMembers = (fieldKeyNameOfRole) => composeValidators(hasArrayItem('少なくとも1人以上のメンバーを追加してください'), (value) => {
    const existsAdmin = (value || []).some((fieldValue) => {
        const role = fieldValue[fieldKeyNameOfRole];
        const roleValue = typeof role === 'string' ? parseInt(role, 10) : role;
        return !!roleValue && isRfpRoleAdmin(roleValue);
    });
    return existsAdmin
        ? undefined
        : `少なくとも1人、役割が「${RFP_OFFER_MEMBER_ROLE_ADMIN.label}」のメンバーが必要です`;
});
/**
 * validation: チーム体制（関わり方）
 * @param isDraft draftCheck の結果受け取り
 */
export const validateOfferMemberOfInvolvement = (isDraft) => composeValidators(isDraft ? null : isRequiredText('関わり方を選択してください'));
/**
 * validation: チーム体制（役割）
 * @param isDraft draftCheck の結果受け取り
 */
export const validateOfferMemberOfRole = (isDraft) => composeValidators(isDraft ? null : isRequiredText('役割を選択してください'));
/**
 * validation: 支払い方法選択
 * @param isDraft draftCheck の結果受け取り
 */
export const validatePaymentOptions = (isDraft) => composeValidators(isDraft ? null : isRequiredText('支払い方法を選択してください'));
/**
 * validation: 請求書送付先のご連絡先
 * @param isDraft draftCheck の結果受け取り
 */
export const validatePaymentBillingInformation = (isDraft) => composeValidators(isDraft
    ? null
    : hasArrayItem('少なくとも1つ以上の請求書送付先のご連絡先を入力してください'));
/**
 * validation: 請求書送付先のご連絡先（姓）
 * @param isDraft draftCheck の結果受け取り
 */
export const validatePaymentBillingInformationOfLastName = (isDraft) => composeValidators(isDraft ? null : isRequiredText('姓を入力してください'), strMax(20, '姓は20文字以内で入力してください'));
/**
 * validation: 請求書送付先のご連絡先（名）
 * @param isDraft draftCheck の結果受け取り
 */
export const validatePaymentBillingInformationOfFirstName = (isDraft) => composeValidators(isDraft ? null : isRequiredText('名を入力してください'), strMax(20, '名は20文字以内で入力してください'));
/**
 * validation: 請求書送付先のご連絡先（メールアドレス）
 * @param isDraft draftCheck の結果受け取り
 */
export const validatePaymentBillingInformationOfEmail = (isDraft) => composeValidators(isDraft ? null : isRequiredText('メールアドレスを入力してください'), isEmail('メールアドレス形式が正しくありません'));
